import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, HostBinding, HostListener, Input, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CLICK_TRACKER } from '@mkp/shared/util-click-tracker';
import { getJobcloudDomain } from '@mkp/shared/util-environment';
import {
  BaseClickCategory,
  CallClickCategory,
  CtaInternalClickCategory,
  DownloadClickCategory,
  ExternalClickCategory,
  GenericClickCategory,
  InternalClickCategory,
  MailClickCategory,
} from '@mkp/tracking/models';

const REDACTED_TEXT = '-redacted-';

@Directive({ selector: '[mkpDataTest]' })
export class DataTestDirective {
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef);
  private readonly clickTracker = inject(CLICK_TRACKER);
  private readonly routerLink = inject(RouterLink, { optional: true });

  @HostBinding('attr.data-test') dataTest = '';
  @Input() trackingCta = false;
  @Input() set mkpDataTest(dataTest: string | undefined) {
    if (!dataTest) {
      console.error(
        `ClickTrackingDirective: no dataTest defined for element ${this.elementRef.nativeElement.innerText}`
      );
    }
    this.dataTest = dataTest ?? '';
  }
  @Input() trackingUrl: string | undefined;
  @Input() trackingValue = '';
  @Input() hideTrackingText = false;
  @Input() trackingDisabled = false;
  @Input() trackingInternalLink = false;

  @HostListener('click') onClick() {
    if (this.dataTest) {
      const { innerText, href } = this.elementRef.nativeElement;
      const url = this.trackingUrl ?? href ?? this.routerLink?.urlTree?.toString();

      const baseCategory = getBaseCategory({
        dataTest: this.dataTest,
        innerText: this.hideTrackingText ? REDACTED_TEXT : cleanText(innerText),
        value: this.trackingValue,
      });
      const clickCategory = !url
        ? this.trackingInternalLink
          ? getInternalClickCategory(this.document.location.href, baseCategory, this.trackingCta)
          : getGenericClickCategory(baseCategory)
        : isMailLink(url)
          ? getMailClickCategory(url, baseCategory, this.hideTrackingText)
          : isCallLink(url)
            ? getCallClickCategory(url, baseCategory, this.hideTrackingText)
            : isDownloadLink(url)
              ? getDownloadClickCategory(url, baseCategory, this.hideTrackingText)
              : isExternalLink(url)
                ? getExternalClickCategory(url, baseCategory)
                : getInternalClickCategory(url, baseCategory, this.trackingCta);

      //CDP-1152
      if (!this.trackingDisabled) {
        this.clickTracker.click(clickCategory);
      }
    } else {
      console.error(`DataTestDirective - sending click event - NO DATATEST`);
    }
  }
}

interface ClickParams {
  dataTest: string;
  innerText: string;
  value: string;
}
const getBaseCategory = ({ dataTest, innerText, value }: ClickParams): BaseClickCategory => ({
  event_name: 'click',
  event_source: 'application',
  event_origin: getJobcloudDomain(),
  event_component_type: dataTest,
  event_link_text: innerText.trim(),
  event_value: value,
});

// innerText will be undefined in jest
const cleanText = (text: string | undefined): string => text?.replace(/\n/g, ' ') ?? '';

// Resolved against the current origin so a relative router link parses too. Comparing
// URL.origin is what keeps a link whose query string carries our own origin external.
const parseUrl = (url: string): URL | undefined => {
  try {
    return new URL(url, window.location.origin);
  } catch {
    return undefined;
  }
};

const getGenericClickCategory = (baseCategory: BaseClickCategory): GenericClickCategory => ({
  ...baseCategory,
  event_name: 'click',
  event_type: 'generic_click',
});

const isMailLink = (url: string): boolean => url.startsWith('mailto:');

const getMailClickCategory = (
  url: string,
  baseCategory: BaseClickCategory,
  hideTrackingText: boolean
): MailClickCategory => ({
  ...baseCategory,
  event_name: 'click',
  event_target_url_full: hideTrackingText ? REDACTED_TEXT : url,
  event_type: 'mail_link',
});

const isCallLink = (url: string): boolean => url.startsWith('tel:');

const getCallClickCategory = (
  url: string,
  baseCategory: BaseClickCategory,
  hideTrackingText: boolean
): CallClickCategory => ({
  ...baseCategory,
  event_name: 'click',
  event_target_url_full: hideTrackingText ? REDACTED_TEXT : url,
  event_type: 'call_link',
});

const isDownloadLink = (url: string) =>
  url.startsWith('https://media') && url.includes('/download/');

const getDownloadClickCategory = (
  url: string,
  baseCategory: BaseClickCategory,
  hideTrackingText: boolean
): DownloadClickCategory => ({
  ...baseCategory,
  event_name: 'click',
  event_target_url_full: hideTrackingText ? REDACTED_TEXT : url,
  event_type: 'download_link',
});

const isExternalLink = (url: string): boolean => {
  const parsed = parseUrl(url);
  const isHttp = parsed?.protocol === 'http:' || parsed?.protocol === 'https:';

  return isHttp && parsed?.origin !== window.location.origin;
};

const getExternalClickCategory = (
  url: string,
  baseCategory: BaseClickCategory
): ExternalClickCategory => ({
  ...baseCategory,
  event_name: 'click',
  event_target_url_full: url,
  event_type: 'external_link',
});

const getInternalLink = (url: string): string =>
  parseUrl(url)?.href ?? `${window.location.origin}${url}`;

const getInternalClickCategory = (
  url: string,
  baseCategory: BaseClickCategory,
  trackingCta: boolean
): InternalClickCategory | CtaInternalClickCategory => ({
  ...baseCategory,
  event_name: trackingCta ? 'cta_click' : 'click',
  event_target_url_full: getInternalLink(url),
  event_type: 'internal_link',
});
